import React, { useMemo } from "react";
import { Icon, Stack, Text } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

interface Props { rows: RawIndicatorRow[] }

// ---- Color Config ----

const SUB_INDICATOR_CONFIG: Record<string, { accent: string; bg: string; border: string; icon: string }> = {
  "Device Health":     { accent: "#1565C0", bg: "#E3F2FD", border: "#90CAF9", icon: "HeartFill" },
  "Device Config":     { accent: "#2E7D32", bg: "#E8F5E9", border: "#A5D6A7", icon: "Settings" },
  "Response Actions":  { accent: "#E65100", bg: "#FFF3E0", border: "#FFCC80", icon: "Shield" },
  "Modern Management": { accent: "#6A1B9A", bg: "#F3E5F5", border: "#CE93D8", icon: "GroupObject" },
  "Coverage Workload": { accent: "#00695C", bg: "#E0F2F1", border: "#80CBC4", icon: "CheckboxComposite" },
};

const DEFAULT_CONFIG = { accent: "#323130", bg: "#F3F2F1", border: "#D0D0D0", icon: "Info" };

const SUB_INDICATOR_ORDER = [
  "Device Health", "Device Config", "Response Actions", "Modern Management", "Coverage Workload",
];

// ---- Helpers ----

const toText = (v: string | number): string => String(v ?? "").trim();

const parseNumber = (v: string | number | undefined): number | undefined => {
  if (v === undefined) return undefined;
  const n = Number(String(v).replace(/[,%]/g, "").trim());
  return Number.isFinite(n) ? n : undefined;
};

const formatDecimal = (v: number | undefined, digits = 1): string =>
  v === undefined ? "—" : v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits });

// ---- Data Types ----

interface FactorRow { label: string; value: string }

interface SubIndicatorData {
  name: string;
  score?: number;
  weight?: string;
  contribution?: string;
  factors: FactorRow[];
  scoringBins?: string;
}

interface MaturitySummary {
  formula: string;
  total?: number;
}

// ---- Parser ----

function parse(rows: RawIndicatorRow[]): {
  subIndicators: SubIndicatorData[];
  summary: MaturitySummary;
} {
  const subMap = new Map<string, SubIndicatorData>();
  let summaryFormula = "";
  let summaryTotal: number | undefined;

  for (const row of rows) {
    const section    = (row.sectionName  ?? "").trim();
    const metricType = (row.metricName   ?? "").trim();
    const label      = (row.factorLabel  ?? "").trim();
    const val        = toText(row.value);

    // Summary row
    if (section === "MaturityHealthScore" && metricType === "Summary") {
      summaryFormula = label;
      const n = parseNumber(row.value);
      if (n !== undefined) summaryTotal = n;
      continue;
    }

    if (!subMap.has(section)) {
      subMap.set(section, { name: section, factors: [] });
    }
    const entry = subMap.get(section)!;

    if (metricType === "Score") {
      if (label === "Score")        entry.score = parseNumber(row.value);
      else if (label === "Weight")  entry.weight = val;
      else if (label === "Contribution") entry.contribution = val;
    } else if (metricType === "Factors") {
      if (/^Scoring Bins:/i.test(label)) {
        entry.scoringBins = val;
      } else if (label && val) {
        // Deduplicate: skip if label already has a matching entry
        const alreadyHas = entry.factors.some(f => f.label === label);
        if (!alreadyHas) {
          entry.factors.push({ label, value: val });
        }
      }
    }
  }

  // Sort sub-indicators by canonical order
  const subIndicators = SUB_INDICATOR_ORDER
    .map(name => subMap.get(name))
    .filter((d): d is SubIndicatorData => d !== undefined);

  // Append any not in canonical order
  subMap.forEach((data, name) => {
    if (!SUB_INDICATOR_ORDER.includes(name)) {
      subIndicators.push(data);
    }
  });

  return {
    subIndicators,
    summary: { formula: summaryFormula, total: summaryTotal },
  };
}

// ---- Score Color ----

const scoreColor = (s: number | undefined): string => {
  if (s === undefined) return "#323130";
  if (s >= 71) return "#107C10";
  if (s >= 41) return "#C15A00";
  return "#D13438";
};

// ---- Factor Box ----

interface FactorBoxProps { label: string; value: string }
const FactorBox: React.FC<FactorBoxProps> = ({ label, value }) => {
  const isYesNo   = value.toLowerCase() === "yes" || value.toLowerCase() === "no";
  const isYes     = value.toLowerCase() === "yes";
  const isRule    = value.toLowerCase() === "rule";
  const valColor  = isYesNo ? (isYes ? "#107C10" : "#A4262C") : "#323130";
  const valText   = isYesNo ? (isYes ? "✓" : "✗") : isRule ? "✓" : value;

  return (
    <Stack
      tokens={{ childrenGap: 2 }}
      styles={{
        root: {
          backgroundColor: "#FFFFFF",
          border: "1px solid #E0E0E0",
          borderRadius: 6,
          padding: "6px 10px",
          minWidth: 80,
        },
      }}
    >
      <Text variant="xSmall" styles={{ root: { color: "#757575", lineHeight: "14px" } }}>{label}</Text>
      <Text variant="medium" styles={{ root: { fontWeight: 700, color: valColor } }}>{valText}</Text>
    </Stack>
  );
};

// ---- Sub-Indicator Card ----

interface SubIndicatorCardProps { data: SubIndicatorData }

const SubIndicatorCard: React.FC<SubIndicatorCardProps> = ({ data }) => {
  const cfg = SUB_INDICATOR_CONFIG[data.name] ?? DEFAULT_CONFIG;
  const sc  = scoreColor(data.score);

  // Separate platform Yes/No from other factors (Coverage Workload)
  const isCoverage = data.name === "Coverage Workload";
  const platformKeys = ["WinServer", "Windows", "Mac", "Linux"];
  const platformFactors = isCoverage ? data.factors.filter(f => platformKeys.includes(f.label)) : [];
  const otherFactors    = isCoverage
    ? data.factors.filter(f => !platformKeys.includes(f.label) && !/^Platform/i.test(f.label) && f.label !== "Rule")
    : data.factors;
  const ruleText = isCoverage ? data.factors.find(f => /^Platform/i.test(f.label))?.value ?? "" : "";

  return (
    <Stack
      tokens={{ childrenGap: 10 }}
      styles={{
        root: {
          backgroundColor: cfg.bg,
          border: `1.5px solid ${cfg.border}`,
          borderTop: `4px solid ${cfg.accent}`,
          borderRadius: 10,
          padding: "14px 16px",
          flex: "1 1 0",
          minWidth: 190,
        },
      }}
    >
      {/* Title */}
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
        <Icon iconName={cfg.icon} styles={{ root: { fontSize: 14, color: cfg.accent } }} />
        <Text variant="small" styles={{ root: { fontWeight: 700, color: cfg.accent } }}>{data.name}</Text>
      </Stack>

      {/* Score / Weight / Contribution */}
      <Stack horizontal tokens={{ childrenGap: 16 }}>
        <Stack tokens={{ childrenGap: 0 }}>
          <Text variant="xSmall" styles={{ root: { color: "#757575" } }}>Score</Text>
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 700, color: sc } }}>
            {data.score !== undefined ? formatDecimal(data.score, 1) : "—"}
          </Text>
        </Stack>
        {data.weight && (
          <Stack tokens={{ childrenGap: 0 }}>
            <Text variant="xSmall" styles={{ root: { color: "#757575" } }}>Weight</Text>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>{data.weight}</Text>
          </Stack>
        )}
        {data.contribution && (
          <Stack tokens={{ childrenGap: 0 }}>
            <Text variant="xSmall" styles={{ root: { color: "#757575" } }}>Contribution</Text>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>{data.contribution}</Text>
          </Stack>
        )}
      </Stack>

      {/* Factors */}
      {(otherFactors.length > 0 || platformFactors.length > 0) && (
        <>
          <div style={{ height: 1, backgroundColor: cfg.border }} />
          <Text variant="xSmall" styles={{ root: { fontWeight: 700, color: "#605E5C" } }}>Factors</Text>

          {/* Coverage Workload: platform grid */}
          {isCoverage && (
            <>
              {ruleText && (
                <Text variant="xSmall" styles={{ root: { color: "#605E5C", fontStyle: "italic" } }}>
                  {ruleText}
                </Text>
              )}
              <Stack horizontal tokens={{ childrenGap: 6 }} wrap>
                {platformFactors.map(f => (
                  <FactorBox key={f.label} label={f.label} value={f.value} />
                ))}
              </Stack>
            </>
          )}

          {/* Other factors as compact key-value list */}
          {otherFactors.length > 0 && (
            <Stack tokens={{ childrenGap: 4 }}>
              {otherFactors.map((f, i) => (
                <Stack key={i} horizontal horizontalAlign="space-between" tokens={{ childrenGap: 8 }}>
                  <Text variant="xSmall" styles={{ root: { color: "#757575", flexShrink: 0 } }}>{f.label}</Text>
                  <Text variant="xSmall" styles={{ root: { fontWeight: 600, color: "#323130", textAlign: "right" } }}>{f.value}</Text>
                </Stack>
              ))}
            </Stack>
          )}
        </>
      )}

      {/* Scoring Bins */}
      {data.scoringBins && (
        <>
          <div style={{ height: 1, backgroundColor: cfg.border }} />
          <Text variant="xSmall" styles={{ root: { color: "#605E5C" } }}>
            <span style={{ fontWeight: 700 }}>Bins: </span>{data.scoringBins}
          </Text>
        </>
      )}
    </Stack>
  );
};

// ---- Main Component ----

const MTPMaturityHealthScoreRawIndicatorCards: React.FC<Props> = ({ rows }) => {
  const { subIndicators, summary } = useMemo(() => parse(rows), [rows]);

  const totalSc = scoreColor(summary.total);

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      {/* Sub-indicator cards */}
      <Stack horizontal wrap tokens={{ childrenGap: 12 }} styles={{ root: { alignItems: "stretch" } }}>
        {subIndicators.map(data => (
          <SubIndicatorCard key={data.name} data={data} />
        ))}
      </Stack>

      {/* Maturity Health Score summary footer */}
      {(summary.formula || summary.total !== undefined) && (
        <Stack
          horizontal
          verticalAlign="center"
          tokens={{ childrenGap: 20 }}
          styles={{
            root: {
              backgroundColor: "#EEF2FB",
              border: "1.5px solid #7B93CA",
              borderRadius: 10,
              padding: "14px 20px",
            },
          }}
        >
          <Icon iconName="BarChart4" styles={{ root: { fontSize: 20, color: "#1565C0" } }} />
          <Stack tokens={{ childrenGap: 2 }} styles={{ root: { flex: 1 } }}>
            <Text variant="small" styles={{ root: { fontWeight: 700, color: "#1565C0" } }}>
              Maturity Health Score
            </Text>
            {summary.formula && (
              <Text variant="xSmall" styles={{ root: { color: "#605E5C", fontFamily: "monospace" } }}>
                {summary.formula}
              </Text>
            )}
          </Stack>
          {summary.total !== undefined && (
            <Stack
              horizontalAlign="center"
              verticalAlign="center"
              styles={{
                root: {
                  backgroundColor: totalSc,
                  borderRadius: 20,
                  padding: "4px 18px",
                  minWidth: 56,
                },
              }}
            >
              <Text variant="large" styles={{ root: { fontWeight: 700, color: "#FFFFFF" } }}>
                {formatDecimal(summary.total, 1)}
              </Text>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default MTPMaturityHealthScoreRawIndicatorCards;
